/**
 * DataLabelsPlugin - uPlot plugin for rendering data value labels on line charts
 *
 * Supports three modes:
 * - 'off': No labels displayed
 * - 'all': Labels displayed for all data points
 * - 'minmax': Labels displayed only for minimum and maximum values per series
 */

import type uPlot from 'uplot';
import type { VizTheme } from './theme';

const LOCALE = 'en-US';

export type DataLabelsMode = 'off' | 'all' | 'minmax';

export interface DataLabelsConfig {
    mode: DataLabelsMode;
    theme: VizTheme;
    /**
     * Hidden series indices (series that should not display labels)
     */
    hiddenSeries?: Set<number>;
}

/**
 * Formats a number value for display on the chart
 * Uses comma separators for thousands and shows up to 2 decimal places
 */
const formatValue = (value: number): string => {
    // Use toLocaleString for comma-separated thousands
    // Show decimals only if the number is not an integer
    if (Number.isInteger(value)) {
        return value.toLocaleString(LOCALE);
    }
    // For decimals, show up to 2 decimal places
    return value.toLocaleString(LOCALE, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
};

/**
 * Finds indices of min and max values in a series
 */
const findMinMaxIndices = (seriesData: uPlot.AlignedData[number]): number[] => {
    const validPoints: Array<{ value: number; index: number }> = [];

    for (let idx = 0; idx < seriesData.length; idx += 1) {
        const value = seriesData[idx];
        if (value != null && !Number.isNaN(value)) {
            validPoints.push({ value: Number(value), index: idx });
        }
    }

    if (validPoints.length === 0) {
        return [];
    }

    // Find min and max values
    let minPoint = validPoints[0];
    let maxPoint = validPoints[0];

    validPoints.forEach(point => {
        if (point.value < minPoint.value) {
            minPoint = point;
        }
        if (point.value > maxPoint.value) {
            maxPoint = point;
        }
    });

    // Return unique indices (in case min === max)
    const indices = [minPoint.index];
    if (maxPoint.index !== minPoint.index) {
        indices.push(maxPoint.index);
    }

    return indices;
};

interface LabelInfo {
    seriesIdx: number;
    dataIdx: number;
    value: number;
    xPos: number;
    yPos: number;
    label: string;
    color: string;
}

interface UsedPosition {
    y: number;
    column: number;
}

/**
 * Determines if a series should be shown
 */
const shouldShowSeries = (
    seriesData: uPlot.AlignedData[number] | undefined,
    seriesIdx: number,
    series: uPlot.Series[],
    hiddenSeries?: Set<number>
): boolean => {
    return !(!seriesData || hiddenSeries?.has(seriesIdx) || !series[seriesIdx]?.show);
};

/**
 * Gets the color for a series
 */
const getSeriesColor = (series: uPlot.Series, theme: VizTheme): string => {
    return typeof series.stroke === 'string' ? series.stroke : theme.axis.yLabelStroke || '#000';
};

/**
 * Gets indices of points to label based on mode
 */
const getPointsToLabel = (mode: DataLabelsMode, seriesData: uPlot.AlignedData[number]): number[] => {
    if (mode === 'all') {
        const points: number[] = [];
        for (let idx = 0; idx < seriesData.length; idx += 1) {
            const value = seriesData[idx];
            if (value != null && !Number.isNaN(value)) {
                points.push(idx);
            }
        }
        return points;
    }
    if (mode === 'minmax') {
        return findMinMaxIndices(seriesData);
    }
    return [];
};

/**
 * Checks if a point is within chart bounds
 */
const isPointInBounds = (xPos: number, yPos: number, bbox: uPlot.BBox): boolean => {
    return !(
        xPos < bbox.left ||
        xPos > bbox.left + bbox.width ||
        yPos < bbox.top ||
        yPos > bbox.top + bbox.height
    );
};

interface CreateLabelInfoOptions {
    u: uPlot;
    seriesIdx: number;
    dataIdx: number;
    value: number;
    seriesColor: string;
}

/**
 * Creates a label info object for a data point
 */
const createLabelInfo = (options: CreateLabelInfoOptions): LabelInfo | null => {
    const { u, seriesIdx, dataIdx, value, seriesColor } = options;
    const { data, series, bbox } = u;

    const xValue = data[0][dataIdx];
    if (xValue == null) {
        return null;
    }

    const scale = series[seriesIdx].scale || 'y';
    const xPosValue = u.valToPos(xValue, 'x', true);
    const yPos = u.valToPos(value, scale, true);

    if (!isPointInBounds(xPosValue, yPos, bbox)) {
        return null;
    }

    return {
        seriesIdx,
        dataIdx,
        value,
        xPos: xPosValue,
        yPos,
        label: formatValue(value),
        color: seriesColor,
    };
};

/**
 * Collects all labels to be rendered
 */
const collectLabels = (u: uPlot, config: DataLabelsConfig): LabelInfo[] => {
    const { data, series } = u;
    const allLabels: LabelInfo[] = [];

    for (let seriesIdx = 1; seriesIdx < series.length; seriesIdx += 1) {
        const seriesData = data[seriesIdx];

        if (shouldShowSeries(seriesData, seriesIdx, series, config.hiddenSeries)) {
            const seriesColor = getSeriesColor(series[seriesIdx], config.theme);
            const pointsToLabel = getPointsToLabel(config.mode, seriesData);

            pointsToLabel.forEach(dataIdx => {
                const value = seriesData[dataIdx];
                if (value == null || Number.isNaN(value)) {
                    return;
                }

                const labelInfo = createLabelInfo({
                    u,
                    seriesIdx,
                    dataIdx,
                    value: value as number,
                    seriesColor,
                });

                if (labelInfo) {
                    allLabels.push(labelInfo);
                }
            });
        }
    }

    return allLabels;
};

/**
 * Groups labels by X position (data index)
 */
const groupLabelsByX = (labels: LabelInfo[]): Map<number, LabelInfo[]> => {
    const labelsByX = new Map<number, LabelInfo[]>();
    labels.forEach(label => {
        const existingLabels = labelsByX.get(label.dataIdx);
        if (existingLabels) {
            existingLabels.push(label);
        } else {
            labelsByX.set(label.dataIdx, [label]);
        }
    });

    // Sort each group by Y position (lowest yPos = highest on screen)
    labelsByX.forEach(group => {
        group.sort((a, b) => a.yPos - b.yPos);
    });

    return labelsByX;
};

/**
 * Maximum number of columns to try for collision avoidance
 */
const MAX_LABEL_COLUMNS = 5;

/**
 * Checks if a label position has collision in a specific column
 */
const hasCollisionInColumn = (
    labelY: number,
    column: number,
    usedPositions: UsedPosition[],
    minVerticalSpacing: number
): boolean => {
    return usedPositions.some(
        used => used.column === column && Math.abs(labelY - used.y) < minVerticalSpacing
    );
};

/**
 * Finds a column position that avoids collisions
 */
const findCollisionFreeColumn = (
    labelY: number,
    usedPositions: UsedPosition[],
    minVerticalSpacing: number
): number => {
    for (let column = 0; column < MAX_LABEL_COLUMNS; column += 1) {
        if (!hasCollisionInColumn(labelY, column, usedPositions, minVerticalSpacing)) {
            return column;
        }
    }
    return MAX_LABEL_COLUMNS - 1; // Return last column if all have collisions
};

/**
 * Calculates label position and alignment
 */
const calculateLabelPosition = (
    labelXPos: number,
    column: number,
    isLeftmost: boolean,
    isRightmost: boolean,
    bbox: uPlot.BBox,
    columnOffset: number
): { labelX: number; textAlign: CanvasTextAlign } => {
    let textAlign: CanvasTextAlign = 'center';
    let labelX = labelXPos + column * columnOffset;

    if (isLeftmost) {
        textAlign = 'left';
        labelX = bbox.left + column * columnOffset;
    } else if (isRightmost) {
        textAlign = 'right';
        labelX = bbox.left + bbox.width - column * columnOffset;
    }

    return { labelX, textAlign };
};

/**
 * Renders labels at a specific X position
 */
const renderLabelsAtX = (
    ctx: CanvasRenderingContext2D,
    labelsAtX: LabelInfo[],
    bbox: uPlot.BBox,
    baseOffset: number,
    dpr: number,
    focusedSeriesIdx: number | null
): void => {
    const firstLabel = labelsAtX[0];
    const { xPos } = firstLabel;
    const chartWidth = bbox.width;
    const edgeThreshold = chartWidth * 0.1;

    const isLeftmost = xPos - bbox.left < edgeThreshold;
    const isRightmost = bbox.left + bbox.width - xPos < edgeThreshold;

    const usedPositions: UsedPosition[] = [];
    const minVerticalSpacing = 16 * dpr;
    const columnOffset = 30 * dpr;
    const isSingleLabel = labelsAtX.length === 1;

    labelsAtX.forEach(labelInfo => {
        const { xPos: labelXPos, yPos, label, color, seriesIdx } = labelInfo;
        const labelY = yPos - baseOffset;

        // Find column position
        let column = 0;
        if (!isSingleLabel) {
            column = findCollisionFreeColumn(labelY, usedPositions, minVerticalSpacing);
        }

        usedPositions.push({ y: labelY, column });

        const { labelX, textAlign } = calculateLabelPosition(
            labelXPos,
            column,
            isLeftmost,
            isRightmost,
            bbox,
            columnOffset
        );

        // Determine opacity based on focused series
        let opacity = 1;
        if (focusedSeriesIdx !== null && focusedSeriesIdx !== seriesIdx) {
            opacity = 0.1; // Fade out non-focused series labels
        }

        // Draw the label with opacity
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.fillStyle = color;
        ctx.textAlign = textAlign;
        ctx.textBaseline = 'middle';
        ctx.fillText(label, labelX, labelY);
        ctx.restore();
    });
};

/**
 * Creates a uPlot plugin that renders data value labels on the chart
 */
export const createDataLabelsPlugin = (config: DataLabelsConfig): uPlot.Plugin => {
    return {
        hooks: {
            draw: (u: uPlot): void => {
                if (config.mode === 'off') {
                    return;
                }

                const { ctx, series, data, bbox } = u;

                if (!data || data.length < 2) {
                    return;
                }

                const dpr = window.devicePixelRatio || 1;
                const fontSize = 10 * dpr;
                const baseOffset = 8 * dpr;

                // Get focused series index by checking series alpha values
                // When a series is focused in uPlot, other series have reduced alpha (< 1)
                let focusedSeriesIdx: number | null = null;

                // Check if we're in a focus state by checking if any series has reduced alpha
                const hasAnyFadedSeries = series.some(
                    (s, idx) => idx > 0 && s.alpha !== undefined && s.alpha < 1
                );

                if (hasAnyFadedSeries) {
                    // Find the series with full opacity (alpha = 1 or undefined)
                    focusedSeriesIdx = series.findIndex(
                        (s, idx) => idx > 0 && (s.alpha === undefined || s.alpha === 1)
                    );
                }

                ctx.save();
                ctx.font = `${fontSize}px ${config.theme.font || 'sans-serif'}`;

                // Collect all labels to render
                const allLabels = collectLabels(u, config);

                // Group labels by X position
                const labelsByX = groupLabelsByX(allLabels);

                // Render labels at each X position
                labelsByX.forEach(labelsAtX => {
                    renderLabelsAtX(ctx, labelsAtX, bbox, baseOffset, dpr, focusedSeriesIdx);
                });

                ctx.restore();
            },
        },
    };
};
